/**
 * A port's status, edited where it reads on the drawing during a script
 * preview: vented to atmosphere, sealed, or a pressure, vacuum or flow of so
 * much. Pressures are typed in kPa gauge and flows in the design's flow
 * unit (D49); what is stored is SI (invariant 5).
 */
import { useEffect, useId, useRef, useState } from "react";
import type { SourceKind } from "../design/protocol";
import { useLiquidUnits } from "../workspace/DisplayUnitsContext";
import { useKeepInside } from "./keepInside";
import { typedNumber } from "../workspace/units";

export interface PortSettingValue {
  kind: SourceKind;
  /** Pascal gauge for pressure and vacuum, m³/s for flow. */
  magnitude: number | null;
}

const KINDS: readonly { value: SourceKind; label: string }[] = [
  { value: "open", label: "open to atmosphere" },
  { value: "sealed", label: "sealed" },
  { value: "pressure", label: "pressure" },
  { value: "vacuum", label: "vacuum" },
  { value: "flow", label: "flow" },
];

/** Display unit and its factor to SI, by kind; flow in the design's unit. */
type Units = Partial<Record<SourceKind, { label: string; factor: number }>>;
const KPA = { label: "kPa", factor: 1000 };

export function PortSettingEditor({
  name,
  initial,
  x,
  y,
  onCommit,
  onCancel,
}: {
  name: string;
  initial: PortSettingValue;
  /** Screen position of the status text's baseline, centred. */
  x: number;
  y: number;
  onCommit: (value: PortSettingValue) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const flow = useLiquidUnits().flowRate;
  const UNIT: Units = { pressure: KPA, vacuum: KPA, flow: { label: flow.label, factor: flow.factor } };
  const [kind, setKind] = useState<SourceKind>(initial.kind);
  const unit = UNIT[kind];
  const shown = (value: number | null, of: SourceKind) => {
    const u = UNIT[of];
    return value === null || !u ? "" : String(Number((Math.abs(value) / u.factor).toPrecision(6)));
  };
  const [text, setText] = useState(shown(initial.magnitude, initial.kind));
  const selectRef = useRef<HTMLSelectElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useKeepInside(formRef);
  useEffect(() => selectRef.current?.focus(), []);

  const parsed = text.trim() === "" ? null : typedNumber(text);
  const valid = !unit || parsed === null || (Number.isFinite(parsed) && parsed >= 0);
  const commit = () => {
    if (!valid) return;
    const magnitude = unit && parsed !== null ? parsed * unit.factor * (kind === "vacuum" ? -1 : 1) : null;
    onCommit({ kind, magnitude });
  };

  return (
    <form
      ref={formRef}
      data-label-editor=""
      className="cx-port-editor"
      aria-label={`Status of ${name}`}
      style={{ left: x, top: y - 12 }}
      onSubmit={(event) => {
        event.preventDefault();
        commit();
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          onCancel();
        }
      }}
      onBlur={(event) => {
        // Leaving the editor altogether, not moving between its fields.
        if (!formRef.current?.contains(event.relatedTarget as Node | null)) onCancel();
      }}
    >
      <label htmlFor={`${id}-kind`} className="visually-hidden">
        Status
      </label>
      <select id={`${id}-kind`} ref={selectRef} value={kind} onChange={(event) => setKind(event.target.value as SourceKind)}>
        {KINDS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {unit && (
        <>
          <label htmlFor={`${id}-value`} className="visually-hidden">
            {kind === "flow" ? "Flow" : "Gauge"} in {unit.label}
          </label>
          {kind === "vacuum" && <span aria-hidden="true">−</span>}
          <input
            id={`${id}-value`}
            inputMode="decimal"
            value={text}
            aria-invalid={!valid || undefined}
            onChange={(event) => setText(event.target.value.replace(/^[-−]/, ""))}
          />
          <span>{unit.label}</span>
        </>
      )}
      {/* WebKit gives a clicked button no focus at all, so pressing Set would blur the field with nowhere going and this form's own onBlur would end editing before the click arrived. Keeping the press from moving focus keeps the click. */}
      <button type="submit" disabled={!valid} onMouseDown={(event) => event.preventDefault()}>
        Set
      </button>
    </form>
  );
}
